import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Dashboard, Widget } from '@detect/shared';
import { dashboardService } from '../../services/dashboards';
import type { WithId } from '../../services/types';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useTenantQuery } from '../../hooks/useApi';
import { LoadingSpinner, ErrorState, EmptyState } from '../../components/common';
import { WidgetCard } from './WidgetCard';
import { WidgetModal } from './WidgetModal';

export function DashboardViewPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const { tenantId, hasRole } = useTenant();
  const [editing, setEditing] = useState<Widget | 'new' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const query = useTenantQuery<WithId<Dashboard>>(['dashboard', id], `/dashboards/${id}`);

  if (query.isLoading) return <LoadingSpinner />;
  if (query.isError || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const dashboard = query.data;
  // Mirrors the API end to end: canManage (owner or admin) AND the
  // route-level agent minimum — an owner demoted below agent loses the
  // affordance, matching the 403 they would get.
  const canEdit =
    hasRole('agent') && (dashboard.ownerUserId === user?.id || hasRole('admin'));

  const saveWidgets = async (widgets: Widget[]) => {
    if (!tenantId) return;
    setActionError(null);
    await dashboardService.update(tenantId, dashboard.id, { widgets });
    void query.refetch();
  };

  const upsertWidget = async (widget: Widget) => {
    const exists = dashboard.widgets.some((w) => w.id === widget.id);
    const widgets = exists
      ? dashboard.widgets.map((w) => (w.id === widget.id ? widget : w))
      : [...dashboard.widgets, widget];
    await saveWidgets(widgets);
    setEditing(null);
  };

  const removeWidget = async (widgetId: string) => {
    if (!window.confirm('Remove this widget?')) return;
    try {
      await saveWidgets(dashboard.widgets.filter((w) => w.id !== widgetId));
    } catch (err) {
      setActionError(err);
    }
  };

  const nextY = dashboard.widgets.reduce(
    (max, w) => Math.max(max, w.position.y + w.position.h),
    0
  );

  /** Drag-to-move / resize commit (#10): clamp to the schema's grid
   *  contract (x+w <= 48) and persist via the same widgets update the
   *  editor modal uses. */
  const moveWidget = async (
    widgetId: string,
    position: { x: number; y: number; w: number; h: number }
  ) => {
    const clamped = {
      w: Math.min(48, Math.max(4, position.w)),
      h: Math.min(100, Math.max(3, position.h)),
      x: 0,
      y: Math.min(1000, Math.max(0, position.y)),
    };
    clamped.x = Math.min(48 - clamped.w, Math.max(0, position.x));
    try {
      await saveWidgets(
        dashboard.widgets.map((w) =>
          w.id === widgetId ? { ...w, position: clamped } : w
        )
      );
    } catch (err) {
      setActionError(err);
    }
  };

  /** JSON export (#10): the portable shape is the CREATE input —
   *  re-importable anywhere, carrying no tenant/owner identity. */
  const exportJson = () => {
    const portable = {
      name: dashboard.name,
      // Exact round-trip: null stays absent, but '' survives, and
      // client scoping is preserved (codex finding 1).
      ...(dashboard.description !== null
        ? { description: dashboard.description }
        : {}),
      ...(dashboard.clientId !== null ? { clientId: dashboard.clientId } : {}),
      shared: dashboard.shared,
      widgets: dashboard.widgets,
    };
    const blob = new Blob([JSON.stringify(portable, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dashboard.name.replace(/[^a-z0-9-_]+/gi, '-')}.dashboard.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <Link to="/dashboards">Dashboards</Link> / {dashboard.name}
        </h1>
        <span className="page-actions">
          <button type="button" className="btn btn-secondary" onClick={exportJson}>
            Export JSON
          </button>
          {canEdit && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setEditing('new')}
            >
              Add widget
            </button>
          )}
        </span>
      </div>
      {actionError !== null && <ErrorState error={actionError} />}

      {dashboard.widgets.length === 0 ? (
        <EmptyState
          title="Empty dashboard"
          description="Add graph, gauge, NOC, alert, or SLA widgets."
        />
      ) : (
        <div className="widget-grid">
          {[...dashboard.widgets]
            .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)
            .map((w) => (
              <WidgetCard
                key={w.id}
                widget={w}
                canEdit={canEdit}
                onEdit={() => setEditing(w)}
                onRemove={() => void removeWidget(w.id)}
                onLayout={(position) => void moveWidget(w.id, position)}
              />
            ))}
        </div>
      )}

      {editing !== null && (
        <WidgetModal
          widget={editing === 'new' ? null : editing}
          nextY={nextY}
          onClose={() => setEditing(null)}
          onSave={upsertWidget}
        />
      )}
    </div>
  );
}
